import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Notification, NotificatonType } from "@/types/globalTypes";
import toast from "react-hot-toast";
import { messaging } from "@/firebase/firebaseConfig";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { setSearchValue, setSelectedSearchOption } from "@/store/searchSlice";
import { useNavigate } from "react-router-dom";
import useServiceWorkers from "@/hooks/useServiceWorkers";
import { useUserRole } from "@/hooks/useUserRole";
import { navigateToSubscribtion } from "@/utils/helpers";
import { MdWavingHand } from "react-icons/md";
import { MdTipsAndUpdates } from "react-icons/md";
import { RiVipDiamondFill } from "react-icons/ri";
import { BsExclamationDiamondFill } from "react-icons/bs";
import { MdCancel } from "react-icons/md";
import { IoCheckmarkCircle } from "react-icons/io5";
import { VscMilestone } from "react-icons/vsc";
import { TbStarsFilled } from "react-icons/tb";


// eslint-disable-next-line react-refresh/only-export-components
export const typeToIcon: Record<NotificatonType, JSX.Element> = {
  welcome: <MdWavingHand size={30} className="text-blue-500" />,
  "system-update": <MdTipsAndUpdates size={30} className="text-blue-500" />,
  "subscription-month-alarm": (
    <RiVipDiamondFill size={30} className="text-yellow-500" />
  ),
  "subscription-week-alarm": (
    <RiVipDiamondFill size={30} className="text-orange-500" />
  ),
  "subscription-day-alarm": (
    <RiVipDiamondFill size={30} className="text-red-500" />
  ),
  "subscription-expired": (
    <BsExclamationDiamondFill size={30} className="text-red-700" />
  ),
  "order-received": <IoCheckmarkCircle size={30} className="text-green-500" />,
  "order-canceled": <MdCancel size={30} className="text-red-500" />,
  "orders-milestone": <VscMilestone size={30} className="text-purple-500" />,
  "order-reviewed": <TbStarsFilled size={30} className="text-yellow-400" />,
};

const OrderRouteNavigation: NotificatonType[] = [
  "order-received",
  "order-canceled",
  "orders-milestone",
];

const SubscriptionRouteNavigation: NotificatonType[] = [
  "subscription-month-alarm",
  "subscription-week-alarm",
  "subscription-day-alarm",
  "subscription-expired",
];

const ReviewRouteNavigation: NotificatonType[] = ["order-reviewed"];

interface NotificationContextType {
  unreadedNotifications: Notification[];
  markAsRead: (ids?: string[]) => void;
  handleMarkAsReadWithSearch: (notification: Notification) => Promise<void>;
  needNotificationsRefetch: boolean;
  setNeedNotificationsRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  markAsReadWithNavigate: (notification: Notification,path:string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // handle Service Workers registration and device token regestration for web & capacitor
  useServiceWorkers();

  const userToken = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const { t } = useTranslation();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const [needNotificationsRefetch, setNeedNotificationsRefetch] =
    useState(false);
  const [unreadedNotifications, setUnreadedNotifications] = useState<
    Notification[]
  >([] as Notification[]);
  const {isAdmin,isBranchAdmin} = useUserRole()
  const isFromAdmins = isAdmin || isBranchAdmin

  const notificationSound = useMemo(
    () => new Audio("/sounds/notificationSound.mp3"),
    [],
  );


  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleNotification = useCallback(
    (
      // payload: unknown
    ) => {
      // console.log("Notification received:", payload);

      notificationSound.currentTime = 0;
      if (notificationSound.paused) {
        notificationSound.play().catch(() => {
          document.addEventListener(
            "click",
            () => {
              notificationSound.play().catch(() => null);
            },
            { once: true },
          );
        });
      }
      setNeedNotificationsRefetch(true);
    },
    [notificationSound],
  );

  useEffect(() => {
    if (!userToken) return;

    setNeedNotificationsRefetch(false);

    async function fetchUnreadedNotifications() {
      const url = `${BASE_URL}/api/dashboard/notifications?status=unread`;

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: userToken,
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          setUnreadedNotifications(response.data.notifications);
        } else {
          setUnreadedNotifications([]);
          return;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          console.error("Error fetching notifications:", error);
        } else {
          console.error("Error fetching notifications:", error);
        }
      }
    }

    fetchUnreadedNotifications();
  }, [BASE_URL, needNotificationsRefetch, userToken]);

  useEffect(() => {
    if (!userToken) return;

    if (Capacitor.getPlatform() === "web") {
      // Web-specific logic
          // Listen for incoming messages
          messaging?.onMessage((payload) => {
            if (!payload.notification) return;
            handleNotification(
              // payload
            );
          });
        }
    else {
      // Android/iOS-specific logic
      PushNotifications.addListener(
        "pushNotificationReceived",
        (
          // notification
        ) => {
          // Check permissions before playing sound
          PushNotifications.checkPermissions().then((permissionStatus) => {
            if (permissionStatus.receive === "granted") {
              handleNotification(
                // notification
              );
            }
          });
        },
      );

      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification) => {
          const type = notification.notification.data.type;

          if (OrderRouteNavigation.includes(type)) {
            navigate("/orders");
          } else if (SubscriptionRouteNavigation.includes(type)) {
            navigateToSubscribtion(navigate, userToken);
          } else if (ReviewRouteNavigation.includes(type)) {
            navigate("/orders/reviews");
          } else {
            navigate("/notifications");
          }
        },
      );
    }
  }, [handleNotification, navigate, userToken]);

  const markAsRead = async (ids?: string[]) => {
    const url = `${BASE_URL}/api/dashboard/notifications/mark-as-read`;

    const loadingToastMessage = ids
      ? (t("notificationContext.markingOne") as string)
      : (t("notificationContext.markingAll") as string);

    const successToastMessage = ids
      ? (t("notificationContext.markedOne") as string)
      : (t("notificationContext.markedAll") as string);

    const errorToastMessage = ids
      ? (t("notificationContext.failedOne") as string)
      : (t("notificationContext.failedAll") as string);

    const loadingToast = toast.loading(loadingToastMessage);

    const body = ids ? { includes: ids } : {};

    try {
      const response = await axios.post(url, body, {
        headers: {
          Authorization: userToken,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        toast.success(successToastMessage);
        setNeedNotificationsRefetch(true);
      } else {
        const errorMessage = response.data.error || errorToastMessage;
        toast.error(errorMessage);
        return;
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error || errorToastMessage;
        // console.error(message);
        toast.error(message);
      } else {
        toast.error("Error marking notifications as read:");
      }
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const markAsReadWithNavigate = async (notification: Notification,path:string) => {
    const isSubscriptionNavigate = path === "/settings/subscribtion";
    
    await markAsRead([notification.id] as string[]).then(() => {
    if(isSubscriptionNavigate){
      navigateToSubscribtion(navigate,userToken);
    }else{
      navigate(path);
    }}).catch((err) => {
      console.error(err); // reject on error
    }); 
  };

  const handleMarkAsReadWithSearch = (
    notification: Notification,
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (notification.type === "order-received") {
        const submitBtn = document.getElementById(
          "searchBtn",
        ) as HTMLButtonElement;

        const performSearch = () => {
          if(!isFromAdmins) return
          dispatch(setSearchValue(notification.meta?.orderId as string));
          dispatch(setSelectedSearchOption("orders"));
          setTimeout(() => {
            submitBtn.click();
            dispatch(setSearchValue(""));
            resolve(); // resolve after the click
          }, 0);
        };

        if (notification.read) {
          performSearch();
        } else {
          markAsRead([notification.id] as string[])
            .then(() => {
              performSearch();
            })
            .catch((err) => {
              reject(err); // reject on error
            });
        }
      } else {
        resolve(); // resolve if notification is not of type "order-received"
      }
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        unreadedNotifications,
        markAsRead,
        handleMarkAsReadWithSearch,
        needNotificationsRefetch,
        setNeedNotificationsRefetch,
        markAsReadWithNavigate
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
//eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};
